import { useEffect, useId, useRef, useState } from 'react'

import {
  BlendingModeIcon,
  CheckIcon,
  ChevronDownIcon,
  MoonIcon,
  SunIcon,
} from '@radix-ui/react-icons'
import { useSelect } from 'downshift'
import { usePopper } from 'react-popper'
import { ComponentProps, GrafbaseThemes } from 'style'

import { useMounted } from 'hooks/use-mounted'
import { useTheme } from 'lib/themes/use-theme'

import Box from 'components/atomic/atoms/box'
import { IconStyled } from 'components/atomic/atoms/icon'
import {
  StyledContainer,
  StyledContent,
  StyledItem,
  StyledItemIndicator,
  StyledTrigger,
} from 'components/atomic/atoms/select/select.styled'
import Text from 'components/atomic/atoms/text'

const themes = [
  { value: GrafbaseThemes.system, icon: BlendingModeIcon },
  { value: GrafbaseThemes.dark, icon: MoonIcon },
  { value: GrafbaseThemes.light, icon: SunIcon },
]

type Props = Pick<ComponentProps<typeof StyledTrigger>, 'size' | 'disabled'>

const Content = ({ size, disabled }: Props) => {
  const { theme, setTheme } = useTheme()
  const id = useId()
  const {
    isOpen,
    selectedItem,
    highlightedIndex,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    getLabelProps,
  } = useSelect({
    id,
    items: themes,
    defaultSelectedItem: themes.find(({ value }) => value === theme),
    onSelectedItemChange: ({ selectedItem: t }) => !!t && setTheme(t.value),
  })

  // Use popper to position the menu
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLUListElement>(null)
  const { styles, attributes } = usePopper(
    buttonRef.current,
    popoverRef.current,
    {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
      ],
    },
  )

  // Dynamically sync trigger and menu widths
  const [contentWidth, setContentWidth] = useState(0)
  const triggerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (triggerRef.current)
      setContentWidth(triggerRef.current.getBoundingClientRect().width)
  }, [])

  return (
    <StyledContainer ref={triggerRef}>
      <Text as="label" {...getLabelProps()} srOnly>
        Choose website theme
      </Text>
      <StyledTrigger
        {...getToggleButtonProps({ ref: buttonRef })}
        type="button"
        size={size}
        disabled={disabled}
      >
        <Box
          as="span"
          flex
          items="center"
          justify="center"
          css={{ spaceX: 2, textTransform: 'capitalize' }}
        >
          <IconStyled as={selectedItem?.icon} />
          <span>{selectedItem?.value}</span>
        </Box>
        <IconStyled
          as={ChevronDownIcon}
          size="md"
          color="text3"
          css={{ rotate: isOpen ? '180deg' : '' }}
        />
      </StyledTrigger>

      {contentWidth > 0 && (
        <StyledContent
          {...getMenuProps({ ref: popoverRef }, { suppressRefError: true })}
          {...attributes.popper}
          size={size}
          style={{
            ...styles.popper,
            width: contentWidth,
          }}
          open={isOpen}
        >
          {isOpen &&
            themes.map((item, index) => {
              const active = selectedItem?.value === item.value
              return (
                <StyledItem
                  key={`${item.value}-${index}`}
                  {...getItemProps({ item, index })}
                  focused={highlightedIndex === index}
                  css={{ textTransform: 'capitalize' }}
                >
                  {active && (
                    <StyledItemIndicator>
                      <IconStyled as={CheckIcon} size="sm" color="text3" />
                    </StyledItemIndicator>
                  )}
                  {item.value}
                </StyledItem>
              )
            })}
        </StyledContent>
      )}
    </StyledContainer>
  )
}

const ThemeSelect = (props: Props) => {
  const mounted = useMounted()

  // Using useSelect without the component being mounted created errors in console
  if (!mounted) {
    return null
  }

  return <Content {...props} />
}

export default ThemeSelect
